import React, { useRef, useState } from 'react';
import { Button } from '../Button';
import { showErrorToast, showSuccessToast } from '../../lib/ToastUtility';
import { getApiErrorCode, getApiErrorMessage } from '../../utils/apiError';
import {
  createAdminKioskRegistration,
  uploadKioskRegistrationImage,
} from '../../services/KioskRegistrationService';
import type {
  AdminCreateKioskRegistrationPayload,
  KioskRegistrationType,
} from '../../types/kioskRegistration.types';
import ModalShell from './ModalShell';
import { INPUT_CLASS, TYPE_LABELS } from './registrationUi';

interface Props {
  onClose: () => void;
  onCreated: (registrationId: number) => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_PATTERN = /^(?=.*[A-Za-z])(?=.*\d).+$/;
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const TYPES: KioskRegistrationType[] = ['INDIVIDUAL', 'AMBASSADOR', 'CORPORATE'];

type FormErrors = Partial<Record<'name' | 'organisationName' | 'email' | 'password' | 'terms', string>>;

const Field = ({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) => (
  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
    {label}
    <div className="mt-1.5">{children}</div>
    {error && <span role="alert" className="mt-1 block text-xs font-normal text-red-600">{error}</span>}
  </label>
);

export default function RegisterOwnerModal({ onClose, onCreated }: Props) {
  const [type, setType] = useState<KioskRegistrationType>('INDIVIDUAL');
  const [name, setName] = useState('');
  const [organisationName, setOrganisationName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [password, setPassword] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [termsAttested, setTermsAttested] = useState(false);
  const [linkExistingUser, setLinkExistingUser] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [existingAccountPrompt, setExistingAccountPrompt] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const isCorporate = type === 'CORPORATE';
  const busy = submitting || uploading;

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setUploadError(null);
    if (!IMAGE_TYPES.includes(file.type)) {
      setUploadError('Please upload a PNG, JPEG or WebP image.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setUploadError('The image must be 2MB or smaller.');
      return;
    }
    setUploading(true);
    try {
      setProfileImageUrl(await uploadKioskRegistrationImage(file));
    } catch (error) {
      setUploadError(getApiErrorMessage(error, 'The image could not be uploaded.'));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const validate = (): FormErrors => {
    const next: FormErrors = {};
    if (!name.trim()) next.name = `${isCorporate ? 'Contact person name' : 'Full name'} is required.`;
    if (isCorporate && !organisationName.trim()) next.organisationName = 'Organisation name is required.';
    if (!email.trim()) next.email = 'Email is required.';
    else if (!EMAIL_PATTERN.test(email.trim())) next.email = 'Enter a valid email address.';
    if (password) {
      if (password.length < 8) next.password = 'Password must be at least 8 characters.';
      else if (!PASSWORD_PATTERN.test(password)) next.password = 'Password must contain at least one letter and one number.';
    }
    if (!isCorporate && !termsAttested) next.terms = 'Confirm the customer has accepted the Terms & Conditions.';
    return next;
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    void register(false);
  };

  const register = async (attachToExistingAccount: boolean) => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const payload: AdminCreateKioskRegistrationPayload = {
      registrationType: type,
      name: name.trim(),
      organisationName: isCorporate ? organisationName.trim() : undefined,
      email: email.trim(),
      phone: phone.trim() || undefined,
      message: message.trim() || undefined,
      profileImageUrl: profileImageUrl || undefined,
      password: linkExistingUser || attachToExistingAccount ? undefined : password || undefined,
      termsAttested: isCorporate ? undefined : termsAttested,
      linkExistingUser: linkExistingUser || attachToExistingAccount || undefined,
    };

    setSubmitting(true);
    setSubmitError(null);
    setExistingAccountPrompt(false);
    try {
      const result = await createAdminKioskRegistration(payload);
      showSuccessToast(result.message || `Registration ${result.referenceCode} created.`);
      onCreated(result.id);
    } catch (error) {
      if (getApiErrorCode(error) === 'EXISTING_ACCOUNT') {
        setExistingAccountPrompt(true);
        return;
      }
      const messageText = getApiErrorMessage(error, 'The registration could not be created.');
      setSubmitError(messageText);
      showErrorToast(messageText);
    } finally {
      setSubmitting(false);
    }
  };

  const imageLabel = isCorporate ? 'Company logo' : 'Display photo';

  return (
    <ModalShell
      title="Register channel owner"
      size="lg"
      onClose={busy ? () => undefined : onClose}
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button size="md" type="submit" form="register-owner-form" loading={submitting} disabled={uploading}>
            Register owner
          </Button>
        </>
      }
    >
      <form id="register-owner-form" onSubmit={submit} noValidate className="space-y-4">
        <div role="radiogroup" aria-label="Registration type" className="grid grid-cols-3 gap-1 rounded-xl bg-gray-100 p-1 dark:bg-slate-900">
          {TYPES.map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={type === value}
              onClick={() => setType(value)}
              className={`rounded-lg px-2 py-2 text-sm font-semibold transition-colors ${
                type === value
                  ? 'bg-white text-emerald-800 shadow-sm dark:bg-slate-700 dark:text-emerald-200'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-300'
              }`}
            >
              {TYPE_LABELS[value]}
            </button>
          ))}
        </div>

        {isCorporate && (
          <Field label="Organisation name" error={errors.organisationName}>
            <input value={organisationName} onChange={(e) => setOrganisationName(e.target.value)} maxLength={255} className={INPUT_CLASS} />
          </Field>
        )}
        <Field label={isCorporate ? 'Contact person name' : 'Full name'} error={errors.name}>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={255} autoComplete="off" className={INPUT_CLASS} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Email" error={errors.email}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} autoComplete="off" className={INPUT_CLASS} />
          </Field>
          <Field label="Phone (optional)">
            <input value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={80} className={INPUT_CLASS} />
          </Field>
        </div>
        <Field label="Message (optional)">
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={5000} rows={3} className={INPUT_CLASS} />
        </Field>

        <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {imageLabel} (optional)
          <div className="mt-1.5 flex items-center gap-4">
            {profileImageUrl ? (
              <img src={profileImageUrl} alt={`${imageLabel} preview`} className="h-16 w-16 rounded-xl border border-gray-200 bg-white object-contain dark:border-white/10" />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-xl border border-dashed border-gray-300 text-xs font-normal text-gray-400 dark:border-white/20">
                No image
              </div>
            )}
            <div className="space-y-1">
              <input
                ref={fileInput}
                type="file"
                accept={IMAGE_TYPES.join(',')}
                onChange={(e) => void handleFile(e.target.files?.[0])}
                disabled={busy}
                aria-label={`Upload ${imageLabel.toLowerCase()}`}
                className="block text-xs font-normal"
              />
              <p className="text-xs font-normal text-gray-500 dark:text-gray-400">PNG, JPEG or WebP, up to 2MB.</p>
              {uploading && <p className="text-xs font-normal text-gray-600">Uploading…</p>}
              {uploadError && <p role="alert" className="text-xs font-normal text-red-600">{uploadError}</p>}
              {profileImageUrl && !uploading && (
                <button type="button" onClick={() => setProfileImageUrl('')} className="text-xs font-semibold text-red-700 hover:underline">
                  Remove image
                </button>
              )}
            </div>
          </div>
        </div>

        {existingAccountPrompt && (
          <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100">
            <p className="font-semibold">This email already has a TrackYourImpact account</p>
            <p className="mt-1">
              We can create the channel under that account. The owner keeps their current email and password and uses them to log in to
              the admin portal, so no new password is created. Their account also becomes a channel owner account, which can limit
              some donor features in the TrackYourImpact app and website.
            </p>
            <p className="mt-1">Only continue if the customer agrees to this.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" onClick={() => void register(true)} loading={submitting}>
                Create under the existing account
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setExistingAccountPrompt(false)} disabled={submitting}>
                Use a different email
              </Button>
            </div>
          </div>
        )}

        {!linkExistingUser && (
          <Field label="Password (optional)" error={errors.password}>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} maxLength={128} autoComplete="new-password" className={INPUT_CLASS} />
            <span className="mt-1 block text-xs font-normal text-gray-500 dark:text-gray-400">
              If left blank, the owner is asked to use “Forgot password” to set one. Minimum 8 characters with a letter and a number.
            </span>
          </Field>
        )}

        {!isCorporate && (
          <div>
            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input type="checkbox" checked={termsAttested} onChange={(e) => setTermsAttested(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300" />
              I confirm the customer has accepted the Tap2Feed Terms &amp; Conditions
            </label>
            {errors.terms && <p role="alert" className="mt-1 text-xs text-red-600">{errors.terms}</p>}
          </div>
        )}

        <details className="rounded-xl border border-gray-200 px-3 py-2 text-sm dark:border-white/10">
          <summary className="cursor-pointer font-medium text-gray-700 dark:text-gray-200">Advanced</summary>
          <label className="mt-3 flex items-start gap-2 text-gray-700 dark:text-gray-200">
            <input type="checkbox" checked={linkExistingUser} onChange={(e) => setLinkExistingUser(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300" />
            Attach the channel to an existing platform account with this email
          </label>
        </details>

        <div className="rounded-xl bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100">
          {isCorporate ? (
            <>
              What happens next: the registration moves to the call stage. The team arranges the call and sends the Zoom link to the
              customer, then sends the proposal and PandaDoc terms.
            </>
          ) : (
            'What happens next: the channel goes live immediately and the welcome email is sent to the owner.'
          )}
        </div>

        {submitError && (
          <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
            {submitError}
          </p>
        )}
      </form>
    </ModalShell>
  );
}
